import React, { useState, useEffect } from 'react';
import { Html5QrcodeScanner } from 'html5-qrcode';
import LocationDropdown from '../../../tabs/Dropdown/LocationName';
import axiosInstance from '../../../../../api';

// EditCaseModal component for editing an existing EMS case
const EditCaseModal = ({ show, onClose, formData, onChange, onSave, isLoading }) => {
  // State for controlling QR code scanner visibility
  const [showScanner, setShowScanner] = useState(false);
  // State for form validation errors
  const [errors, setErrors] = useState({});
  // State for selected location ID
  const [selectedLocation, setSelectedLocation] = useState(formData.case_location || '');
  // State for selected location name (for EMS Requester)
  const [selectedLocationName, setSelectedLocationName] = useState(formData.case_location_name || '');
  // State for selected area ID
  const [selectedArea, setSelectedArea] = useState(formData.case_area || '');
  // State for accident place
  const [accidentPlace, setAccidentPlace] = useState(formData.accident_place || '');
  // State for managing file input fields for document uploads
  const [fileInputs, setFileInputs] = useState([]);
  // State for available medical centers based on selected area
  const [medicalCenters, setMedicalCenters] = useState([]);
  // State for available areas based on selected location
  const [areas, setAreas] = useState([]);
  // State for available patient areas based on selected patient location
  const [patientAreas, setPatientAreas] = useState([]);
  // State for all areas fetched from the API
  const [allAreas, setAllAreas] = useState([]);
  // State for tracking loading status of areas
  const [loadingAreas, setLoadingAreas] = useState(false);
  // State for area fetch error
  const [areaError, setAreaError] = useState(null);
  // State for loading medical centers
  const [loadingMedicalCenters, setLoadingMedicalCenters] = useState(false);
  // Check user role from sessionStorage with case-insensitive comparison
  const roleName = sessionStorage.getItem('Role_Name') || '';
  const isEMSRequester = roleName.toLowerCase() === 'ems requester';
  // Track file validity for disabling submit button
  const [isFileValid, setIsFileValid] = useState(true);

   const [selectedPatientLocation, setSelectedPatientLocation] = useState(formData.patient_location || '');
  const [selectedPatientArea, setSelectedPatientArea] = useState(formData.patient_area || '');

  // Debugging logs for role and location
  useEffect(() => {
    console.log('EditCaseModal: isEMSRequester =', isEMSRequester, 'Role_Name =', roleName, 
                'selectedLocation =', selectedLocation, 'selectedLocationName =', selectedLocationName);
  }, [roleName, selectedLocation, selectedLocationName]);

  // Auto-select location and fetch name for EMS Requester
  useEffect(() => {
    if (show && isEMSRequester) {
      const userLocationId = sessionStorage.getItem('User_Location_Id') || '';
      if (userLocationId && userLocationId !== selectedLocation) {
        setSelectedLocation(userLocationId);
        onChange({ target: { name: 'case_location', value: userLocationId } });
        // Fetch location name
        const fetchLocationName = async () => {
          try {
            const response = await axiosInstance.get(`/ems/locations/${userLocationId}`);
            setSelectedLocationName(response.data.location_name || 'Unknown Location');
            console.log('EditCaseModal: Auto-selected location:', userLocationId, 'Name:', response.data.location_name);
          } catch (err) {
            console.error('Error fetching location name:', err);
            setSelectedLocationName('Unknown Location');
          }
        };
        fetchLocationName();
      }
    }
  }, [show, isEMSRequester, onChange]);

  // Maximum number of allowed file uploads
  const MAX_FILES = 3;

  // Predefined list of injury types for the datalist
  const injuryTypes = [
    'Fracture', 'Laceration', 'Burn', 'Sprain', 'Strain',
    'Concussion', 'Dislocation', 'Abrasion', 'Contusion',
    'Puncture Wound', 'Amputation', 'Crush Injury'
  ];

  // Initialize QR code scanner when showScanner is true
  useEffect(() => {
    let scanner;
    if (showScanner) {
      scanner = new Html5QrcodeScanner("reader", { fps: 10, qrbox: 250 });
      scanner.render((decodedText) => {
        onChange({ target: { name: 'bracelet_id', value: decodedText } });
        setShowScanner(false);
        scanner.clear();
      });
    }
    return () => {
      if (scanner) scanner.clear();
    };
  }, [showScanner, onChange]);

  // Reset form fields and file inputs when modal is shown
  useEffect(() => {
    if (show) {
      if (!isEMSRequester) {
        setSelectedLocation(formData.case_location || '');
        setSelectedLocationName(formData.case_location_name || '');
      }
      setSelectedArea(formData.case_area || '');
      setSelectedPatientLocation(formData.patient_location || '');
      setSelectedPatientArea(formData.patient_area || '');
      setAccidentPlace(formData.accident_place || '');
      setErrors({});
      if (formData.documents) {
        const docKeys = Object.keys(formData.documents).filter(key => key.startsWith('documents'));
        const docCount = docKeys.length;
        if (docCount > 0) {
          setFileInputs(Array.from({ length: Math.min(docCount, MAX_FILES) }, (_, i) => i));
        } else {
          setFileInputs([0]);
        }
      } else {
        setFileInputs([0]);
      }
      if (formData.case_area) {
        fetchMedicalCenters(formData.case_area);
      }
    }
  }, [show, formData, isEMSRequester]);

  // Fetch all areas when component mounts
  useEffect(() => {
    const fetchAllAreas = async () => {
      try {
        setLoadingAreas(true);
        setAreaError(null);
        const response = await axiosInstance.get('/ems/areas');
        setAllAreas(response.data || []);
      } catch (err) {
        console.error('Error fetching areas:', err);
        setAreaError('Failed to load areas. Please try again.');
      } finally {
        setLoadingAreas(false);
      }
    };
    fetchAllAreas();
  }, []);

  // Filter areas based on selected location
  useEffect(() => {
    if (!selectedLocation) {
      setAreas([]);
      setSelectedArea('');
      onChange({ target: { name: 'case_area', value: '' } });
      onChange({ target: { name: 'nearest_medical_center', value: '' } });
      return;
    }
    const filtered = allAreas.filter(area => area.location_id == selectedLocation);
    setAreas(filtered);
  }, [selectedLocation, allAreas, onChange]);

  // Filter patient areas based on selected patient location
  useEffect(() => {
    if (!selectedPatientLocation) {
      setPatientAreas([]);
      setSelectedPatientArea('');
      onChange({ target: { name: 'patient_area', value: '' } });
      return;
    }
    const filtered = allAreas.filter(area => area.location_id == selectedPatientLocation);
    setPatientAreas(filtered);
  }, [selectedPatientLocation, allAreas, onChange]);

  // Fetch medical centers when area changes
  useEffect(() => {
    if (selectedArea) {
      fetchMedicalCenters(selectedArea);
    } else {
      setMedicalCenters([]);
    }
  }, [selectedArea]);

  // Function to fetch medical centers
  const fetchMedicalCenters = async (areaId) => {
    try {
      setLoadingMedicalCenters(true);
      const authToken = sessionStorage.getItem('auth_token') || 
        '1059|qI6krv8rol5UGTuG2cWffcX57wSz6YlWEh5i68vXca519965';
      const response = await axiosInstance.get(
        `/ems/get-nearby-medical-center-by-area/${areaId}`,
        {
          headers: {
            Accept: 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      setMedicalCenters(response.data || []);
    } catch (err) {
      console.error('Error fetching medical centers:', err);
      if (formData.nearest_medical_center) {
        setMedicalCenters([{ 
          id: formData.nearest_medical_center, 
          name: 'Previously Selected Center', 
          distance_km: 'N/A' 
        }]);
      } else {
        setMedicalCenters([]);
      }
    } finally {
      setLoadingMedicalCenters(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    onClose();
  };

  // Handle location selection change
  const handleLocationChange = (e) => {
    const value = e.target.value;
    setSelectedLocation(value);
    setSelectedArea('');
    onChange({ target: { name: 'case_location', value } });
    onChange({ target: { name: 'case_area', value: '' } });
    onChange({ target: { name: 'nearest_medical_center', value: '' } });
    if (value) {
      setErrors(prev => ({ ...prev, case_location: '' }));
    } else {
      setErrors(prev => ({ 
        ...prev, 
        case_location: 'Please select a location', 
        case_area: 'Please select a location first' 
      }));
    }
  };

  // Handle patient location selection change
  const handlePatientLocationChange = (e) => {
    const value = e.target.value;
    setSelectedPatientLocation(value);
    setSelectedPatientArea('');
    onChange({ target: { name: 'patient_location', value } });
    onChange({ target: { name: 'patient_area', value: '' } });
    if (value) {
      setErrors(prev => ({ ...prev, patient_location: '' }));
    } else {
      setErrors(prev => ({ 
        ...prev, 
        patient_location: 'Please select a patient location', 
        patient_area: 'Please select a patient location first' 
      }));
    }
  };

  // Handle area selection change
  const handleAreaChange = async (e) => {
    const value = e.target.value;
    setSelectedArea(value);
    onChange({ target: { name: 'case_area', value } });
    onChange({ target: { name: 'nearest_medical_center', value: '' } });
    if (value) {
      setErrors(prev => ({ ...prev, case_area: '' }));
    }
  };

  // Handle patient area selection change
  const handlePatientAreaChange = async (e) => {
    const value = e.target.value;
    setSelectedPatientArea(value);
    onChange({ target: { name: 'patient_area', value } });
    if (value) {
      setErrors(prev => ({ ...prev, patient_area: '' }));
    }
  };

  // Handle accident place change
  const handleAccidentPlaceChange = (e) => {
    const value = e.target.value;
    setAccidentPlace(value);
    onChange({ target: { name: 'accident_place', value } });
    if (value) {
      setErrors(prev => ({ ...prev, accident_place: '' }));
    }
  };

  // Handle form field changes with validation
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Validation for specific fields
    if (name === 'case_name') {
      // Allow only letters and spaces
      if (/^[a-zA-Z\s]*$/.test(value) || value === '') {
        onChange(e);
        setErrors(prev => ({ ...prev, [name]: '' }));
      } else {
        setErrors(prev => ({
          ...prev,
          [name]: 'Case Name must contain only letters and spaces',
        }));
        return;
      }
    } else if (name === 'injury_type') {
      // Allow only letters and spaces
      if (/^[a-zA-Z\s]*$/.test(value) || value === '') {
        onChange(e);
        setErrors(prev => ({ ...prev, [name]: '' }));
      } else {
        setErrors(prev => ({
          ...prev,
          [name]: 'Injury Type must contain only letters and spaces',
        }));
        return;
      }
    } else if (name === 'contact_number') {
      // Allow only numbers
      if (/^\d*$/.test(value) || value === '') {
        onChange(e);
        setErrors(prev => ({ ...prev, [name]: '' }));
      } else {
        setErrors(prev => ({
          ...prev,
          [name]: 'Contact Number must contain only numbers',
        }));
        return;
      }
    } else {
      // For other fields, update without specific validation
      onChange(e);
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle file input changes for document uploads
  const handleFileChange = (e, index) => {
    const file = e.target.files[0];
    const newFiles = { ...(formData.documents || {}) };
    let valid = true;

    if (file) {
      const validTypes = [
        'application/pdf',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/webp',
        'image/jfif'
      ];

      if (!validTypes.includes(file.type)) {
        setErrors(prev => ({
          ...prev,
          [`documents${index + 1}`]: 'Only PDF, JPG, JPEG, PNG, WEBP, and JFIF files are allowed',
        }));
        setIsFileValid(false);
        valid = false;
        return;
      }

      newFiles[`documents${index + 1}`] = file;
    } else {
      delete newFiles[`documents${index + 1}`];
    }

    onChange({ target: { name: 'documents', value: newFiles } });

    // Clear error if valid
    setErrors(prev => ({ ...prev, [`documents${index + 1}`]: '' }));

    // Check all current files for validity
    const allFilesValid = Object.values(newFiles).every(
      f =>
        !f ||
        ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/jfif'].includes(f.type)
    );
    setIsFileValid(allFilesValid);
  };

  // Add a new file input field
  const addFileInput = () => {
    if (fileInputs.length < MAX_FILES) {
      const newIndex = fileInputs.length;
      setFileInputs([...fileInputs, newIndex]);
    }
  };

  // Remove a file input field and update documents
  const removeFileInput = (index) => {
    const newFileInputs = fileInputs.filter((_, i) => i !== index);
    setFileInputs(newFileInputs.map((_, i) => i));
    const newFiles = { ...(formData.documents || {}) };
    delete newFiles[`documents${index + 1}`];
    const updatedFiles = {};
    let counter = 1;
    Object.values(newFiles).forEach((file) => {
      if (file) {
        updatedFiles[`documents${counter}`] = file;
        counter++;
      }
    });
    onChange({ target: { name: 'documents', value: updatedFiles } });
    if (newFileInputs.length === 0 && MAX_FILES > 0) {
      setFileInputs([0]);
    }
  };

  // Validate form fields before submission
  // Inside validateForm() function – replace the existing one
const validateForm = () => {
  const newErrors = {};
  let isValid = true;

  // 1. Case Name
  if (!formData.case_name?.trim()) {
    newErrors.case_name = 'Case name is required';
    isValid = false;
  } else if (!/^[a-zA-Z\s]+$/.test(formData.case_name)) {
    newErrors.case_name = 'Case Name must contain only letters and spaces';
    isValid = false;
  }

  // 2. Case Location & Area
  if (!selectedLocation) {
    newErrors.case_location = 'Location is required';
    isValid = false;
  }
  if (!selectedArea) {
    newErrors.case_area = 'Area is required';
    isValid = false;
  }

  // 3. NEW: Patient Location (Required)
  if (!selectedPatientLocation) {
    newErrors.patient_location = 'Patient Location is required';
    isValid = false;
  }

  // 4. NEW: Patient Area (Required if Patient Location is selected)
  if (selectedPatientLocation && !selectedPatientArea) {
    newErrors.patient_area = 'Patient Area is required';
    isValid = false;
  }

  // 5. NEW: Accident Place (Required)
  if (!accidentPlace?.trim()) {
    newErrors.accident_place = 'Accident Place is required';
    isValid = false;
  }

  // 6. Medical Center
  if (!formData.nearest_medical_center) {
    newErrors.nearest_medical_center = 'Medical center is required';
    isValid = false;
  }

  // 7. Injury Type
  if (!formData.injury_type?.trim()) {
    newErrors.injury_type = 'Type of injury is required';
    isValid = false;
  } else if (!/^[a-zA-Z\s]+$/.test(formData.injury_type)) {
    newErrors.injury_type = 'Injury Type must contain only letters and spaces';
    isValid = false;
  }

  // 8. Contact Number (optional but must be numeric if provided)
  if (formData.contact_number?.trim() && !/^\d+$/.test(formData.contact_number)) {
    newErrors.contact_number = 'Contact number must contain only numbers';
    isValid = false;
  }

  // 9. Triage Card & Bracelet ID
  if (!formData.triage_card) {
    newErrors.triage_card = 'Triage card is required';
    isValid = false;
  }
  if (!formData.bracelet_id?.trim()) {
    newErrors.bracelet_id = 'Bracelet ID is required';
    isValid = false;
  }

  // 10. File validation
  if (formData.documents) {
    Object.entries(formData.documents).forEach(([key, file]) => {
      if (file && !['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/jfif'].includes(file.type)) {
        newErrors[key] = 'Only PDF, JPG, JPEG, PNG, WEBP, and JFIF files are allowed';
        isValid = false;
      }
    });
  }

  setErrors(newErrors);
  return isValid;
};

  // Handle form submission
  const handleSave = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const formDataToSend = new FormData();
      formDataToSend.append('case_name', formData.case_name || '');
      formDataToSend.append('case_location', selectedLocation);
      formDataToSend.append('case_area', selectedArea);
      formDataToSend.append('patient_location', selectedPatientLocation);
      formDataToSend.append('patient_area', selectedPatientArea);
      formDataToSend.append('accident_place', formData.accident_place || '');
      formDataToSend.append('nearest_medical_center', formData.nearest_medical_center || '');
      formDataToSend.append('injury_type', formData.injury_type || '');
      formDataToSend.append('age', formData.age || '');
      formDataToSend.append('gender', formData.gender || '');
      formDataToSend.append('contact_number', formData.contact_number || '');
      formDataToSend.append('triage_card', formData.triage_card || '');
      formDataToSend.append('bracelet_id', formData.bracelet_id || '');
      formDataToSend.append('_method', 'PUT');
      if (formData.documents) {
        Object.entries(formData.documents).forEach(([key, file]) => {
          if (file instanceof File) {
            formDataToSend.append(key, file);
          }
        });
      }
      onSave(formDataToSend);
    }
  };

  // Fallback wrapper for LocationDropdown to enforce disabling and display correct name
  const LocationDropdownWrapper = isEMSRequester ? (
    <select
      className={`form-select ${errors.case_location ? 'is-invalid' : ''}`}
      disabled={true}
      value={selectedLocation || ''}
      onChange={handleLocationChange}
    >
      <option value={selectedLocation}>{selectedLocationName || 'Loading Location...'}</option>
    </select>
  ) : (
    <LocationDropdown
      value={selectedLocation}
      onChange={handleLocationChange}
      className={errors.case_location ? 'is-invalid' : ''}
      required
      disabled={isEMSRequester}
    />
  );

  // Render nothing if modal is not shown
  if (!show) return null;

  // Render the modal
  return (
    <>
      <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto' }}>
        <div className="modal-dialog modal-dialog-scrollable modal-lg">
          <div className="modal-content">
            <div className="modal-header d-flex align-items-center">
              <h5 className="modal-title">Edit EMS Case</h5>
              <button type="button" className="btn-close" onClick={handleClose} aria-label="Close"></button>
            </div>
            <div className="modal-body">
              {/* Case Name */}
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label>Case Name <span className="text-danger">*</span></label>
                    <input 
                      type="text" 
                      className={`form-control ${errors.case_name ? 'is-invalid' : ''}`}
                      name="case_name"
                      value={formData.case_name || ''}
                      onChange={handleChange}
                      pattern="[a-zA-Z\s]*"
                      title="Case Name must contain only letters and spaces"
                      required
                    />
                    {errors.case_name && <div className="invalid-feedback">{errors.case_name}</div>}
                  </div>
                </div>
                {/* Location Dropdown */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label>Location <span className="text-danger">*</span></label>
                    {LocationDropdownWrapper}
                    {errors.case_location && <div className="invalid-feedback">{errors.case_location}</div>}
                  </div>
                </div>
                
                {/* Area */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label>Area <span className="text-danger">*</span></label>
                    <select
                      name="case_area"
                      value={selectedArea || ''}
                      onChange={handleAreaChange}
                      className={`form-select ${errors.case_area ? 'is-invalid' : ''}`}
                      disabled={isEMSRequester}
                      required
                    >
                      <option value="">{loadingAreas ? 'Loading areas...' : 'Select Area'}</option>
                      {areaError ? (
                        <option value="" disabled>Error loading areas</option>
                      ) : (
                        areas.map((area) => (
                          <option key={area.id} value={area.id}>
                            {area.area_name} ({area.ems_location?.location_name || 'Unknown'})
                          </option>
                        ))
                      )}
                    </select>
                    {errors.case_area && <div className="invalid-feedback">{errors.case_area}</div>}
                  </div>
                </div>

                {/* Patient Location */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label>Patient Location</label>
                    <LocationDropdown
                      value={selectedPatientLocation}
                      onChange={handlePatientLocationChange}
                      className={errors.patient_location ? 'is-invalid' : ''}
                      disabled={isEMSRequester}
                    />
                    {errors.patient_location && <div className="invalid-feedback">{errors.patient_location}</div>}
                  </div>
                </div>

                {/* Patient Area */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label>Patient Area</label>
                    <select
                      name="patient_area"
                      value={selectedPatientArea || ''}
                      onChange={handlePatientAreaChange}
                      className={`form-select ${errors.patient_area ? 'is-invalid' : ''}`}
                      disabled={isEMSRequester}
                    >
                      <option value="">{loadingAreas ? 'Loading areas...' : 'Select Patient Area'}</option>
                      {areaError ? (
                        <option value="" disabled>Error loading areas</option>
                      ) : (
                        patientAreas.map((area) => (
                          <option key={area.id} value={area.id}>
                            {area.area_name} ({area.ems_location?.location_name || 'Unknown'})
                          </option>
                        ))
                      )}
                    </select>
                    {errors.patient_area && <div className="invalid-feedback">{errors.patient_area}</div>}
                  </div>
                </div>

                {/* Accident Place */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label>Accident Place</label>
                    <input 
                      type="text" 
                      className={`form-control ${errors.accident_place ? 'is-invalid' : ''}`}
                      name="accident_place"
                      value={accidentPlace || ''}
                      onChange={handleAccidentPlaceChange}
                      placeholder="Enter accident location details"
                    />
                    {errors.accident_place && <div className="invalid-feedback">{errors.accident_place}</div>}
                  </div>
                </div>
              </div>

              {/* Medical Center */}
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label>Transfer To <span className="text-danger">*</span></label>
                    <select
                      name="nearest_medical_center"
                      className={`form-control form-select ${errors.nearest_medical_center ? 'is-invalid' : ''}`}
                      value={formData.nearest_medical_center || ''}
                      onChange={handleChange}
                      disabled={isEMSRequester}
                      required
                    >
                      <option value="">{loadingMedicalCenters ? 'Loading medical centers...' : 'Select Medical Center'}</option>
                      {medicalCenters.map((center) => (
                        <option key={center.id} value={center.id}>
                          {center.name} ({center.distance_km || 'N/A'} km)
                        </option>
                      ))}
                    </select>
                    {errors.nearest_medical_center && <div className="invalid-feedback">{errors.nearest_medical_center}</div>}
                  </div>
                </div>

                {/* Injury Type */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label>Type of Injury <span className="text-danger">*</span></label>
                    <input 
                      type="text" 
                      className={`form-control ${errors.injury_type ? 'is-invalid' : ''}`}
                      name="injury_type"
                      value={formData.injury_type || ''}
                      onChange={handleChange}
                      list="injuryTypes"
                      pattern="[a-zA-Z\s]*"
                      title="Injury Type must contain only letters and spaces"
                      required
                    />
                    <datalist id="injuryTypes">
                      {injuryTypes.map((type, index) => (
                        <option key={index} value={type} />
                      ))}
                    </datalist>
                    {errors.injury_type && <div className="invalid-feedback">{errors.injury_type}</div>}
                  </div>
                </div>

                {/* Age */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label>Age</label>
                    <input 
                      type="number" 
                      className={`form-control ${errors.age ? 'is-invalid' : ''}`}
                      name="age"
                      value={formData.age || ''}
                      onChange={handleChange}
                      min="0"
                      max="120"
                    />
                    {errors.age && <div className="invalid-feedback">{errors.age}</div>}
                  </div>
                </div>

                {/* Gender */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label>Gender</label>
                    <select 
                      className={`form-control form-select ${errors.gender ? 'is-invalid' : ''}`} 
                      name="gender"
                      value={formData.gender || ''}
                      onChange={handleChange}
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                    {errors.gender && <div className="invalid-feedback">{errors.gender}</div>}
                  </div>
                </div>

                {/* Contact Number */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label>Contact Number</label>
                    <input 
                      type="tel" 
                      className={`form-control ${errors.contact_number ? 'is-invalid' : ''}`} 
                      name="contact_number"
                      value={formData.contact_number || ''}
                      onChange={handleChange}
                      pattern="\d*"
                      title="Contact Number must contain only numbers"
                    />
                    {errors.contact_number && <div className="invalid-feedback">{errors.contact_number}</div>}
                  </div>
                </div>

                {/* Triage Card */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label>Triage Card <span className="text-danger">*</span></label>
                    <select 
                      className={`form-control form-select ${errors.triage_card ? 'is-invalid' : ''}`}
                      name="triage_card"
                      value={formData.triage_card || ''}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Triage Card</option>
                      <option value="Red">Red</option>
                      <option value="Yellow">Yellow</option>
                      <option value="Green">Green</option>
                      <option value="Black">Black</option>
                    </select>
                    {errors.triage_card && <div className="invalid-feedback">{errors.triage_card}</div>}
                  </div>
                </div>

                {/* Bracelet ID */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label>Bracelet ID <span className="text-danger">*</span></label>
                    <div className="d-flex align-items-center">
                      <input 
                        type="text" 
                        className={`form-control ${errors.bracelet_id ? 'is-invalid' : ''}`}
                        name="bracelet_id"
                        value={formData.bracelet_id || ''}
                        onChange={handleChange}
                        required
                      />
                      <i
                        className="ti ti-scan text-primary fs-4 ms-3 d-lg-none d-block"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setShowScanner(true)}
                      ></i>
                    </div>
                    {errors.bracelet_id && <div className="invalid-feedback">{errors.bracelet_id}</div>}
                  </div>
                </div>
              </div>

              {/* Document Uploads */}
              <div className="row">
                <div className="col-md-12">
                  <label>Documents Upload</label>
                  <p className="text-muted small mb-2">
                    Allowed file types: PDF, JPG, JPEG, PNG, WEBP, JFIF. Maximum 3 files.
                  </p>
                  {fileInputs.map((_, index) => (
                    <div key={index} className="mb-3 d-flex align-items-center flex-nowrap">
                      <div className="flex-grow-1 me-2">
                        <input
                          className={`form-control ${errors[`documents${index + 1}`] ? 'is-invalid' : ''}`}
                          type="file"
                          onChange={(e) => handleFileChange(e, index)}
                          accept="image/jpeg,image/jpg,image/png,image/webp,image/jfif,application/pdf"
                        />
                        {errors[`documents${index + 1}`] && (
                          <div className="invalid-feedback">{errors[`documents${index + 1}`]}</div>
                        )}
                      </div>
                      <div className="d-flex align-items-center">
                        {index === fileInputs.length - 1 && fileInputs.length < MAX_FILES && (
                          <button
                            type="button"
                            className="btn btn-sm btn-icon fs-5 me-2"
                            onClick={addFileInput}
                          >
                            <i className="ti ti-circle-plus text-success"></i>
                          </button>
                        )}
                        {fileInputs.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-sm fs-5 btn-icon"
                            onClick={() => removeFileInput(index)}
                          >
                            <i className="ti ti-trash text-danger"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-outline-danger" 
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-primary" 
                onClick={handleSave}
                disabled={isLoading || !isFileValid}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    Updating...
                  </>
                ) : 'Update'}
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* QR Code Scanner Modal */}
      {showScanner && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.8)', zIndex: '1055' }}>
          <div className="d-flex justify-content-end p-3">
            <i
              className="ti ti-x text-white fs-2"
              style={{ cursor: 'pointer' }}
              onClick={() => setShowScanner(false)}
            ></i>
          </div>
          <div className="d-flex justify-content-center align-items-center" style={{ height: '90%' }}>
            <div id="reader" className="border rounded" style={{ width: '100%', maxWidth: '400px' }}></div>
          </div>
        </div>
      )}
    </>
  );
};

export default EditCaseModal;